'use client';

import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';

export interface MemoSectionProps {
  /** Section number / step indicator (e.g. "1", "2", "3"). */
  number?: ReactNode;
  /** Section title. */
  title: ReactNode;
  /** Body content of the section. */
  children: ReactNode;
  /** Accent tone for the left rule + number. Default 'primary'. */
  tone?: 'primary' | 'success' | 'warning' | 'error' | 'neutral';
  /** Sx overrides on the outer Box. */
  sx?: SxProps<Theme>;
}

function toneAccent(theme: Theme, tone: NonNullable<MemoSectionProps['tone']>) {
  if (tone === 'neutral') return theme.palette.text.secondary;
  return theme.palette[tone].main;
}

export function MemoSection({ number, title, children, tone = 'primary', sx }: MemoSectionProps) {
  const theme = useTheme();
  const accent = toneAccent(theme, tone);

  return (
    <Box
      component="section"
      sx={composeSx(
        {
          position: 'relative',
          pl: 2,
          py: 0.5,
          my: 2,
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 6,
            bottom: 6,
            width: 2,
            borderRadius: 2,
            backgroundColor: alpha(accent, 0.6),
          },
        },
        sx,
      )}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        {number != null && number !== '' && (
          <Typography
            component="span"
            variant="overline"
            sx={{
              color: accent,
              fontWeight: 700,
              letterSpacing: '0.12em',
              lineHeight: 1,
            }}
          >
            {number}
          </Typography>
        )}
        <Typography
          variant="h6"
          component="h2"
          sx={{
            color: 'text.primary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          color: 'text.primary',
          '& > p': { mb: 1 },
          '& > p:last-child': { mb: 0 },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

MemoSection.displayName = 'MemoSection';

export default MemoSection;
